import axios from "axios";
import {
  FETCH_CONVERSATIONS,
  FETCH_CONVERSATION,
  CREATE_CONVERSATION,
  UPDATE_CONVERSATION,
  DELETE_CONVERSATION,
  FETCH_MESSAGES,
  SEND_MESSAGE,
  DELETE_MESSAGE,
  ADD_PARTICIPANT,
  REMOVE_PARTICIPANT,
  FETCH_UNREAD_COUNT,
  MARK_AS_READ,
  UPLOAD_MESSAGE_ATTACHMENT,
  DELETE_MESSAGE_ATTACHMENT,
  MESSAGING_ERROR,
  MESSAGING_LOADING,
  START_LOADING,
  END_LOADING,
} from "./constants";

import * as api from "../api/index.js";

// Helper function to handle errors
const handleError = (error, dispatch) => {
  console.error("Error:", error);
  dispatch({
    type: MESSAGING_ERROR,
    payload: error.response?.data?.message || "An error occurred",
  });
  dispatch({ type: MESSAGING_LOADING });
};

// Action Creators

// Fetch all conversations for the current user
export const fetchConversations = () => async (dispatch) => {
  try {
    dispatch({ type: MESSAGING_LOADING });
    const { data } = await api.fetchConversations();

    dispatch({ type: FETCH_CONVERSATIONS, payload: data });
  } catch (error) {
    handleError(error, dispatch);
  }
};

// Fetch a single conversation by ID
export const fetchConversation = (id) => async (dispatch) => {
  try {
    dispatch({ type: MESSAGING_LOADING });
    const { data } = await api.fetchConversation(id);

    dispatch({ type: FETCH_CONVERSATION, payload: data });
  } catch (error) {
    handleError(error, dispatch);
  }
};

// Create a new conversation
export const createConversation = (conversationData) => async (dispatch) => {
  try {
    dispatch({ type: START_LOADING });
    
    const { data } = await api.createConversation(conversationData);
    
    dispatch({ type: CREATE_CONVERSATION, payload: data });
    
    // After creating a conversation, fetch the updated list
    dispatch(fetchConversations());
    
    return data;
  } catch (error) {
    console.error('Error creating conversation:', error);
    dispatch({ type: MESSAGING_ERROR, payload: error.message });
    throw error;
  } finally {
    dispatch({ type: END_LOADING });
  }
};

// Update an existing conversation
export const updateConversation = (id, updateData) => async (dispatch) => {
  try {
    dispatch({ type: MESSAGING_LOADING });
    const { data } = await api.updateConversation(id, updateData);

    dispatch({ type: UPDATE_CONVERSATION, payload: data });
    return data;
  } catch (error) {
    handleError(error, dispatch);
  }
};

// Delete a conversation
export const deleteConversation = (id) => async (dispatch) => {
  try {
    dispatch({ type: MESSAGING_LOADING });
    await api.deleteConversation(id);

    dispatch({ type: DELETE_CONVERSATION, payload: id });
  } catch (error) {
    handleError(error, dispatch);
  }
};

// Fetch messages for a conversation
export const fetchMessages =
  (conversationId, page = 1, limit = 50) =>
  async (dispatch) => {
    try {
      dispatch({ type: MESSAGING_LOADING });
      const { data } = await api.fetchMessages(conversationId, page, limit);

      dispatch({
        type: FETCH_MESSAGES,
        payload: {
          conversationId,
          messages: data.messages,
          hasMore: data.hasMore,
          page,
        },
      });
    } catch (error) {
      handleError(error, dispatch);
    }
  };

// Send a new message in a conversation
export const sendMessage =
  (conversationId, messageData) => async (dispatch) => {
    try {
      dispatch({ type: MESSAGING_LOADING });
      const { data } = await api.sendMessage(conversationId, messageData);

      dispatch({
        type: SEND_MESSAGE,
        payload: { conversationId, message: data },
      });
      return data;
    } catch (error) {
      handleError(error, dispatch);
    }
  };

// Delete a message
export const deleteMessage =
  (conversationId, messageId) => async (dispatch) => {
    try {
      dispatch({ type: MESSAGING_LOADING });
      await api.deleteMessage(conversationId, messageId);

      dispatch({
        type: DELETE_MESSAGE,
        payload: { conversationId, messageId },
      });
    } catch (error) {
      handleError(error, dispatch);
    }
  };

// Add a participant to a conversation
export const addParticipant =
  (conversationId, userData) => async (dispatch) => {
    try {
      dispatch({ type: MESSAGING_LOADING });
      const { data } = await api.addParticipant(conversationId, userData);

      dispatch({
        type: ADD_PARTICIPANT,
        payload: { conversationId, participant: data },
      });
      return data;
    } catch (error) {
      handleError(error, dispatch);
    }
  };

// Remove a participant from a conversation
export const removeParticipant =
  (conversationId, userId) => async (dispatch) => {
    try {
      dispatch({ type: MESSAGING_LOADING });
      await api.removeParticipant(conversationId, userId);

      dispatch({
        type: REMOVE_PARTICIPANT,
        payload: { conversationId, userId },
      });
    } catch (error) {
      handleError(error, dispatch);
    }
  };

// Fetch unread message count
export const fetchUnreadCount = () => async (dispatch) => {
  try {
    const { data } = await api.fetchUnreadCount();

    dispatch({ type: FETCH_UNREAD_COUNT, payload: data });
  } catch (error) {
    handleError(error, dispatch);
  }
};

// Mark conversation as read
export const markConversationAsRead = (conversationId) => async (dispatch) => {
  try {
    await api.markAsRead(conversationId);

    dispatch({ type: MARK_AS_READ, payload: conversationId });
  } catch (error) {
    handleError(error, dispatch);
  }
};

// Upload message attachment
export const uploadMessageAttachment =
  (conversationId, formData) => async (dispatch) => {
    try {
      dispatch({ type: MESSAGING_LOADING });
      const { data } = await api.uploadMessageAttachment(
        conversationId,
        formData
      );

      dispatch({
        type: UPLOAD_MESSAGE_ATTACHMENT,
        payload: { conversationId, attachment: data },
      });
      return data;
    } catch (error) {
      handleError(error, dispatch);
    }
  };

// Delete message attachment
export const deleteMessageAttachment =
  (conversationId, attachmentId) => async (dispatch) => {
    try {
      dispatch({ type: MESSAGING_LOADING });
      await api.deleteMessageAttachment(conversationId, attachmentId);

      dispatch({
        type: DELETE_MESSAGE_ATTACHMENT,
        payload: { conversationId, attachmentId },
      });
    } catch (error) {
      handleError(error, dispatch);
    }
  };
